import React, { Fragment, memo, useLayoutEffect, useMemo, useRef } from 'react';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { enableAllPlugins, produce } from 'immer';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import tailwindConfig from 'stylesheets/tailwind.config';
import voca from 'voca';

import useSize from 'components/hooks/useSize';

import ConditionalWrapper from 'components/application/ConditionalWrapper';
import OptionChoice from 'components/application/OptionChoice';
import Options from 'components/application/Options';
import DotSkeleton from 'components/application/tableSkeletons/DotSkeleton';
import DotTextSkeleton from 'components/application/tableSkeletons/DotTextSkeleton';
import TextSkeleton from 'components/application/tableSkeletons/TextSkeleton';
import Tooltip from 'components/application/Tooltip';

import { useProjectsDashboardContext } from 'components/contexts/ProjectsDashboardContext';
import { useBreadBoard } from 'components/contexts/Toaster';
import ApprovalStatusIndicator from 'components/projects/components/ApprovalStatusIndicator';
import LifecycleStatusIndicator from 'components/projects/components/LifecycleStatusIndicator';
import ReviewStatusIndicator from 'components/projects/components/ReviewStatusIndicator';
import RiskAssessmentsStatusIndicator from 'components/projects/components/RiskAssessmentsStatusIndicator';
import SignatureStatusIndicator from 'components/projects/components/SignatureStatusIndicator';
import {
  dashboardConfigurableFieldKey,
  formatDomainUserFullName,
} from 'components/projectsDashboard/helpers';
import useDocumentAllowance from 'components/projectsDashboard/hooks/useDocumentAllowance';

import ArchiveIcon from '-!svg-react-loader?name=ArchiveIcon!icons/archive.svg';
import CheckIcon from '-!svg-react-loader?name=CheckMarkIcon!icons/check.svg';
import ContentCopyIcon from '-!svg-react-loader?name=ContentCopyIcon!icons/content_copy.svg';
import MinusIcon from '-!svg-react-loader?name=MinusIcon!icons/minus.svg';
import SortAppliedChangeIcon from '-!svg-react-loader?name=SortAppliedChangeIcon!icons/sort-applied-change.svg';
import SortAppliedIcon from '-!svg-react-loader?name=SortAppliedIcon!icons/sort-applied.svg';
import SortReversedChangeIcon from '-!svg-react-loader?name=SortReversedChangeIcon!icons/sort-reversed-change.svg';
import SortReversedIcon from '-!svg-react-loader?name=SortReversedIcon!icons/sort-reversed.svg';
import SortUnsortChangeIcon from '-!svg-react-loader?name=SortUnsortChangeIcon!icons/sort-unsort-change.svg';
import ThreeDotsIcon from '-!svg-react-loader?name=ThreeDotsIcon!icons/three-dots.svg';
import TrashIcon from '-!svg-react-loader?name=TrashIcon!icons/ic-trash.svg';

import { resourceShape } from 'components/helpers/serialisableResources';

const { colors } = tailwindConfig.theme;

enableAllPlugins();

export default function ProjectsTable({
  columnOrder,
  columnSizing,
  columnSort,
  columnVisibility,
  columns,
  domainProjects,
  folderId,
  isDataPending,
  isProjectDuplicationBlocked,
  onColumnSizingChange,
  onColumnSortChange,
  onDocumentAllowanceLimitReached,
  onProjectActionsClick,
  onProjectArchiveClick,
  onProjectDestroyClick,
  onProjectDuplicateClick,
  onProjectToggle,
  onSelectAllProjectsToggle,
  selectedProjectIds,
}) {
  const { projectDuplicationPolicy } = useProjectsDashboardContext();

  const domainProjectsData = useMemo(() => {
    const data = domainProjects?.data || [];

    return isDataPending ? Array(data.length || 25).fill({}) : data || [];
  }, [isDataPending, domainProjects]);

  const domainProjectsIncluded = useMemo(
    () => domainProjects?.included || {},
    [domainProjects],
  );

  const projectFieldIndexedByOriginalField = useMemo(() => {
    const data = domainProjects?.data || [];
    const included = domainProjects?.included || {};

    return data.reduce((projectMap, project) => {
      projectMap[project.id] =
        project.relationships.dashboardConfigurableFields?.data.reduce(
          (fieldMap, dashboardConfigurableField) => {
            const field = included.field[dashboardConfigurableField.id];
            fieldMap[field.relationships.original_field.data.id] = field;

            return fieldMap;
          },
          {},
        );

      return projectMap;
    }, {});
  }, [domainProjects]);

  const table = useReactTable({
    data: domainProjectsData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    onColumnSizingChange,
    state: {
      columnOrder:
        folderId ?
          columnOrder.filter((columnId) => columnId !== 'folder')
        : columnOrder,
      columnSizing,
      columnVisibility:
        folderId ? { ...columnVisibility, folder: false } : columnVisibility,
    },
    initialState: {
      columnPinning: {
        left: ['selector', 'live', 'project'],
        right: ['actions'],
      },
    },
    meta: {
      columnSort,
      included: domainProjectsIncluded,
      projectFieldIndexedByOriginalField,
      isSortEnabled: selectedProjectIds.length === 0,
      isDataPending,
      isProjectDuplicationBlocked,
      onColumnSortChange,
      onDocumentAllowanceLimitReached,
      onProjectToggle,
      onProjectActionsClick,
      onProjectArchiveClick,
      onProjectDestroyClick,
      onProjectDuplicateClick,
      onSelectAllProjectsToggle,
      projectDuplicationPolicy,
      selectedProjectIds,
    },
  });

  const tableColumnSizeVars = useMemo(
    () =>
      table.getFlatHeaders().reduce((acc, header) => {
        acc[`--col-${header.column.id}-size`] = header.column.getSize();

        return acc;
      }, {}),
    [
      table.getState().columnOrder,
      table.getState().columnSizingInfo,
      table.getState().columnSizing,
      table.getState().columnVisibility,
    ],
  );

  const wrapperRef = useRef();
  const { width: wrapperWidth = 0 } = useSize(wrapperRef);
  const tableWidth = table.getTotalSize();
  const bufferWidth = Math.max(0, wrapperWidth - tableWidth);

  useLayoutEffect(() => {
    if (wrapperWidth) {
      table.setColumnSizing(buildColumnResizing({ table, wrapperWidth }));
    }
  }, [table, wrapperWidth]);

  return (
    <div ref={wrapperRef}>
      <div className='tw-relative tw-w-full tw-overflow-y-auto'>
        <table
          className='tw-table-fixed tw-border-separate tw-select-none tw-border-1 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-grey-100 tw-bg-white'
          style={{ ...tableColumnSizeVars, width: tableWidth }}
        >
          <ProjectsTableHead bufferWidth={bufferWidth} table={table} />
          {table.getState().columnSizingInfo.isResizingColumn ?
            <MemoizedProjectsTableBody
              bufferWidth={bufferWidth}
              table={table}
            />
          : <ProjectsTableBody bufferWidth={bufferWidth} table={table} />}
        </table>
      </div>
    </div>
  );
}

ProjectsTable.propTypes = {
  columnOrder: PropTypes.array.isRequired,
  columnSizing: PropTypes.object.isRequired,
  columnSort: PropTypes.object.isRequired,
  columnVisibility: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  domainProjects: PropTypes.shape({
    data: PropTypes.arrayOf(resourceShape('project')),
    included: PropTypes.object,
  }),
  folderId: PropTypes.string,
  isDataPending: PropTypes.bool.isRequired,
  isProjectDuplicationBlocked: PropTypes.bool.isRequired,
  onColumnSizingChange: PropTypes.func.isRequired,
  onColumnSortChange: PropTypes.func.isRequired,
  onDocumentAllowanceLimitReached: PropTypes.func.isRequired,
  onProjectActionsClick: PropTypes.func.isRequired,
  onProjectArchiveClick: PropTypes.func.isRequired,
  onProjectDestroyClick: PropTypes.func.isRequired,
  onProjectDuplicateClick: PropTypes.func.isRequired,
  onProjectToggle: PropTypes.func.isRequired,
  onSelectAllProjectsToggle: PropTypes.func.isRequired,
  selectedProjectIds: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function buildColumnResizing({ table, wrapperWidth }) {
  return table.getFlatHeaders().reduce((acc, header) => {
    if (
      header.column.id === 'project' &&
      table.getLeftTotalSize() > wrapperWidth / 2
    ) {
      acc[header.column.id] =
        header.column.getSize() - (table.getLeftTotalSize() - wrapperWidth / 2);
    } else {
      acc[header.column.id] = header.column.getSize();
    }
    return acc;
  }, {});
}

function buildColumnStyle({
  column,
  isPinnedColumnVerticalLineVisible,
  rowIndex,
}) {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumnVerticalLineVisible =
    isPinned === 'left' &&
    column.getIsLastColumn('left') &&
    isPinnedColumnVerticalLineVisible;
  const isFirstRightPinnedColumnVerticalLineVisible =
    isPinned === 'right' &&
    column.getIsFirstColumn('right') &&
    isPinnedColumnVerticalLineVisible;

  return {
    boxShadow:
      isLastLeftPinnedColumnVerticalLineVisible ?
        `-2px 0 ${colors.grey[100]} inset`
      : isFirstRightPinnedColumnVerticalLineVisible ?
        `2px 0 ${colors.grey[100]} inset`
      : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: `calc(var(--col-${column?.id}-size) * 1px)`,
    zIndex: isPinned ? 999 - rowIndex : 0,
  };
}

function ProjectsTableHead({ bufferWidth, table }) {
  const { selectedProjectIds } = table.options.meta;

  return (
    <thead>
      {table.getHeaderGroups().map((headerGroup, rowIndex) => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => (
            <Fragment key={header.id}>
              {header.column.getIsPinned() === 'right' &&
                header.column.getIsFirstColumn('right') && (
                  <th
                    className={classNames(
                      selectedProjectIds.length > 0 ?
                        'tw-bg-blue-050'
                      : 'tw-bg-grey-050',
                    )}
                    data-element-name='buffer-header-cell'
                    key='buffer'
                    style={{ width: bufferWidth }}
                  />
                )}
              <th
                className={classNames(
                  selectedProjectIds.length > 0 ?
                    'tw-bg-blue-050'
                  : 'tw-bg-grey-050',
                )}
                style={buildColumnStyle({
                  column: header.column,
                  isPinnedColumnVerticalLineVisible: bufferWidth === 0,
                  rowIndex,
                })}
              >
                <div className='tw-relative'>
                  <div className='tw-truncate tw-text-s tw-font-medium tw-tracking-auto tw-text-grey-900'>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </div>
                  {header.column.getCanResize() && (
                    <div className='tw-group/resize-wrapper tw-absolute tw-right-0 tw-top-0 tw-h-[42px] tw-w-2 tw-cursor-col-resize'>
                      <div
                        className='tw-hidden tw-h-[42px] tw-border-0 tw-border-r-2 tw-border-solid tw-border-blue-500 group-hover/resize-wrapper:tw-block'
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                      />
                    </div>
                  )}
                </div>
              </th>
            </Fragment>
          ))}
        </tr>
      ))}
    </thead>
  );
}

function ProjectsTableBody({ bufferWidth, table }) {
  const { selectedProjectIds } = table.options.meta;

  return (
    <tbody>
      {table.getRowModel().rows.map((row, rowIndex) => (
        <tr className='tw-group/row' key={row.id}>
          {row.getVisibleCells().map((cell) => (
            <Fragment key={cell.id}>
              {cell.column.getIsPinned() === 'right' &&
                cell.column.getIsFirstColumn('right') && (
                  <td
                    className={classNames(
                      'tw-h-13 tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100',
                      selectedProjectIds.includes(row.original.id) ?
                        'tw-bg-blue-025'
                      : 'tw-bg-white hover:tw-bg-blue-025 group-hover/row:tw-bg-blue-025',
                    )}
                    data-element-name='buffer-data-cell'
                    key='buffer'
                    style={{ width: bufferWidth }}
                  />
                )}
              <td
                className={classNames(
                  'tw-h-13 tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100',
                  selectedProjectIds.includes(row.original.id) ?
                    'tw-bg-blue-025'
                  : 'tw-bg-white hover:tw-bg-blue-025 group-hover/row:tw-bg-blue-025',
                )}
                style={buildColumnStyle({
                  column: cell.column,
                  isPinnedColumnVerticalLineVisible: bufferWidth === 0,
                  rowIndex,
                })}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            </Fragment>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

const MemoizedProjectsTableBody = memo(
  ProjectsTableBody,
  (prev, next) =>
    prev.bufferWidth === next.bufferWidth &&
    prev.table.options.data === next.table.options.data,
);

function ProjectsTableHeader({
  columnId,
  columnName,
  columnSort,
  isSortEnabled,
  isSortVisible = true,
  onColumnSortChange,
}) {
  const handleMouseLeave = () => {
    if (!columnSort.isFutureDirectionShownOnHover) {
      onColumnSortChange({
        ...columnSort,
        isFutureDirectionShownOnHover: true,
      });
    }
  };

  const handleSortIconClick = ({ direction, isUnsorted = false }) => {
    onColumnSortChange(
      isUnsorted ?
        {
          columnId: 'lastUpdated',
          direction: 'applied',
          isFutureDirectionShownOnHover: true,
          isUnsorted: true,
        }
      : {
          columnId,
          direction,
          isFutureDirectionShownOnHover: false,
          isUnsorted: false,
        },
    );
  };

  return (
    <div
      className={classNames(
        'tw-group/container tw-flex tw-h-10 tw-items-center tw-justify-start tw-border-1 tw-border-solid tw-border-transparent tw-bg-transparent tw-p-2',
        isSortEnabled &&
          'tw-cursor-pointer hover:tw-border-blue-300 hover:tw-bg-blue-025',
      )}
      onMouseLeave={handleMouseLeave}
    >
      <div className='tw-truncate'>{columnName}</div>
      {isSortVisible && (
        <div
          className={classNames(
            'tw-flex tw-w-full tw-max-w-[28px] tw-justify-end',
            isSortEnabled && 'group-hover/container:tw-hidden',
          )}
        >
          {(() => {
            if (
              columnSort.columnId === columnId &&
              columnSort.direction === 'applied'
            ) {
              return (
                <SortAppliedIcon
                  data-element-name='sort-applied-icon'
                  height={24}
                  width={24}
                />
              );
            } else if (
              columnSort.columnId === columnId &&
              columnSort.direction === 'reversed'
            ) {
              return (
                <SortReversedIcon
                  data-element-name='sort-reversed-icon'
                  height={24}
                  width={24}
                />
              );
            } else {
              return null;
            }
          })()}
        </div>
      )}
      {isSortEnabled &&
        (columnSort.columnId === columnId ||
          columnSort.isFutureDirectionShownOnHover) && (
          <div className='tw-hidden tw-w-full tw-max-w-[28px] tw-justify-end group-hover/container:tw-flex'>
            <div className='tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-sm hover:tw-bg-blue-050'>
              {(() => {
                if (columnSort.columnId === columnId) {
                  if (columnSort.direction === 'applied') {
                    return columnSort.isFutureDirectionShownOnHover ?
                        <SortReversedChangeIcon
                          data-element-name='sort-reversed-change-icon'
                          height={24}
                          onClick={() =>
                            handleSortIconClick({ direction: 'reversed' })
                          }
                          width={24}
                        />
                      : <SortAppliedIcon
                          data-element-name='sort-applied-icon'
                          height={24}
                          onClick={() =>
                            handleSortIconClick({ direction: 'reversed' })
                          }
                          width={24}
                        />;
                  } else if (columnSort.direction === 'reversed') {
                    return (
                      columnSort.isFutureDirectionShownOnHover ?
                        columnId === 'lastUpdated' ?
                          <SortAppliedChangeIcon
                            data-element-name='sort-applied-change-icon'
                            height={24}
                            onClick={() =>
                              handleSortIconClick({ direction: 'applied' })
                            }
                            width={24}
                          />
                        : <SortUnsortChangeIcon
                            data-element-name='sort-unsort-change-icon'
                            height={24}
                            onClick={() =>
                              handleSortIconClick({ isUnsorted: true })
                            }
                            width={24}
                          />
                      : <SortReversedIcon
                          data-element-name='sort-reversed-icon'
                          height={24}
                          onClick={() =>
                            handleSortIconClick(
                              columnId === 'lastUpdated' ?
                                { direction: 'applied' }
                              : { isUnsorted: true },
                            )
                          }
                          width={24}
                        />
                    );
                  }
                } else if (columnSort.isFutureDirectionShownOnHover) {
                  return (
                    <SortAppliedChangeIcon
                      data-element-name='sort-applied-change-icon'
                      height={24}
                      onClick={() =>
                        handleSortIconClick({ direction: 'applied' })
                      }
                      width={24}
                    />
                  );
                }
              })()}
            </div>
          </div>
        )}
    </div>
  );
}

function ProjectsTableRowActions({ row, singularRAMSName, table }) {
  if (table.options.meta.isDataPending) return null;

  const {
    isProjectDuplicationBlocked,
    onDocumentAllowanceLimitReached,
    onProjectActionsClick,
    onProjectArchiveClick,
    onProjectDestroyClick,
    onProjectDuplicateClick,
    projectDuplicationPolicy,
    selectedProjectIds,
  } = table.options.meta;

  const breadBoard = useBreadBoard();

  const { handleDocumentAllowanceCheck, isDocumentAllowanceCheckPending } =
    useDocumentAllowance({
      onError: breadBoard.addInedibleToast,
      onLimitNotReached: () => {
        onProjectDuplicateClick({
          id: row.original.id,
        });
      },
      onLimitReached: onDocumentAllowanceLimitReached,
    });

  const isActionsShown =
    selectedProjectIds.length === 0 ||
    (selectedProjectIds.length === 1 &&
      selectedProjectIds[0] === row.original.id);

  return (
    isActionsShown && (
      <div
        className='tw-p-2'
        data-element-name='projects-dashboard-row-options'
      >
        <Options
          align='right'
          btnToggle={(isOpen) => (
            <div
              className={classNames(
                'tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg',
                isOpen ?
                  'tw-border-1 tw-border-solid tw-border-blue-100 tw-bg-white'
                : 'hover:tw-bg-blue-050',
              )}
            >
              <ThreeDotsIcon
                className='[&_path]:tw-fill-grey-500 [&_path]:group-hover/row:tw-fill-grey-700'
                width={12}
              />
              <span className='tw-sr-only'>Options</span>
            </div>
          )}
          listStyle='tw-w-57 [&_li]:tw-h-auto [&_li]:tw-border-none'
          onOpen={() =>
            onProjectActionsClick({
              id: row.original.id,
            })
          }
          toggleStyle='[&_ul]:tw-border-blue-100 [&_ul]:tw-bg-grey-025 [&_ul]:tw-rounded-lg'
        >
          {projectDuplicationPolicy.canCreate && (
            <ConditionalWrapper
              condition={isProjectDuplicationBlocked}
              wrapper={(children) => (
                <Tooltip
                  className='tooltip-dark--max-w-xxs'
                  placement='left'
                  tooltip={`This ${singularRAMSName} can't be duplicated because it uses categories, risk assessments or tasks which are no longer available`}
                  trigger='hover'
                >
                  {children}
                </Tooltip>
              )}
            >
              <OptionChoice disabled={isProjectDuplicationBlocked}>
                <button
                  className='tw-group tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-tl-lg tw-rounded-tr-lg tw-border-none tw-bg-white tw-p-3 tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-bg-blue-025 disabled:tw-cursor-not-allowed disabled:tw-bg-grey-050 disabled:tw-text-grey-300'
                  data-element-name='duplicate-project'
                  disabled={
                    isDocumentAllowanceCheckPending ||
                    isProjectDuplicationBlocked
                  }
                  onClick={handleDocumentAllowanceCheck}
                >
                  <div className='tw-flex tw-gap-2'>
                    <ContentCopyIcon
                      className={classNames(
                        'tw-flex-none',
                        isProjectDuplicationBlocked ?
                          '[&_path]:tw-fill-grey-300'
                        : '[&_path]:tw-fill-grey-700',
                      )}
                      height={20}
                      width={20}
                    />
                    Duplicate {singularRAMSName}
                  </div>
                </button>
              </OptionChoice>
            </ConditionalWrapper>
          )}
          <OptionChoice>
            <button
              className='tw-group tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-tl-lg tw-rounded-tr-lg tw-border-none tw-bg-white tw-p-3 tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-bg-blue-025'
              data-element-name='archive-project'
              onClick={() =>
                onProjectArchiveClick({
                  id: row.original.id,
                })
              }
            >
              <div className='tw-flex tw-gap-2'>
                <ArchiveIcon
                  className='[&_path]:tw-fill-grey-700'
                  height={20}
                  width={20}
                />
                Archive {singularRAMSName}
              </div>
            </button>
          </OptionChoice>
          <OptionChoice>
            <button
              className='tw-group tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-bl-lg tw-rounded-br-lg tw-border-none tw-bg-white tw-p-3 tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-bg-red-025 hover:tw-text-red-700'
              data-element-name='delete-project'
              onClick={() =>
                onProjectDestroyClick({
                  id: row.original.id,
                })
              }
            >
              <div className='tw-flex tw-gap-2'>
                <TrashIcon
                  className='[&_path]:tw-fill-grey-700 [&_path]:group-hover:tw-fill-red-700'
                  height={20}
                  width={20}
                />
                Delete {singularRAMSName}
              </div>
            </button>
          </OptionChoice>
        </Options>
      </div>
    )
  );
}

export function buildColumnNames({
  dashboardConfigurableFields,
  singularRAMSName,
}) {
  const columnNames = {
    live: 'Live',
    project: voca.capitalize(singularRAMSName),
    folder: 'Folder',
    reference: 'Reference',
    lastUpdated: 'Last updated',
    riskAssessments: 'Risk assessments',
    reviewDate: 'Review date',
    approvalStatus: 'Approval',
    creator: 'Creator',
    creationDate: 'Created',
    startDate: 'Start date',
    reviewer: 'Reviewer',
    signatureStatus: 'Signature',
    approver: 'Approver',
  };

  dashboardConfigurableFields.forEach((dashboardConfigurableField) => {
    const columnId = dashboardConfigurableFieldKey({
      dashboardConfigurableField,
    });

    columnNames[columnId] = voca.capitalize(
      dashboardConfigurableField.attributes.label,
    );
  });

  return columnNames;
}

export function buildProjectTableConfiguration({
  dashboardConfigurableFields,
  isApprovalConfigurable,
  isFolderConfigurable,
  isReviewConfigurable,
  isRiskAssessmentConfigurable,
  isSignatureConfigurable,
  singularRAMSName,
  tableColumnConfigurations = [],
  tableSortConfiguration = null,
}) {
  const columnNames = buildColumnNames({
    dashboardConfigurableFields,
    singularRAMSName,
  });

  const hiddenColumns = new Set();
  if (!isApprovalConfigurable)
    ['approvalStatus', 'approver'].forEach((c) => hiddenColumns.add(c));
  if (!isFolderConfigurable) hiddenColumns.add('folder');
  if (!isReviewConfigurable)
    ['reviewDate', 'reviewer'].forEach((c) => hiddenColumns.add(c));
  if (!isRiskAssessmentConfigurable) hiddenColumns.add('riskAssessments');
  if (!isSignatureConfigurable) hiddenColumns.add('signatureStatus');

  const formattedTableColumnConfigurations = tableColumnConfigurations.reduce(
    (acc, tableColumnConfiguration) => {
      const columnName = tableColumnConfiguration.attributes.columnType;
      const columnId =
        columnName === 'dashboard_configurable_field' ?
          dashboardConfigurableFieldKey({
            dashboardConfigurableField:
              tableColumnConfiguration.relationships.field.data,
          })
        : voca.camelCase(columnName);

      acc.set(columnId, tableColumnConfiguration.attributes.width);

      return acc;
    },
    new Map(),
  );

  const formattedTableSortConfiguration =
    tableSortConfiguration &&
    (() => {
      const columnName = tableSortConfiguration.attributes.columnType;
      const columnId =
        columnName === 'dashboard_configurable_field' ?
          dashboardConfigurableFieldKey({
            dashboardConfigurableField:
              tableSortConfiguration.relationships.field.data,
          })
        : voca.camelCase(columnName);

      return {
        columnId,
        direction: tableSortConfiguration.attributes.direction,
      };
    })();

  return produce({ isReset: false }, (draftState) => {
    assignColumnOrder({ draftState });
    assignColumnVisibility({ draftState });
    assignColumnSort({ draftState });
    assignColumnSizingDefaults({ draftState });
    assignColumnSizing({ draftState });
    assignColumns({ draftState });
  });

  function assignColumnOrder({ draftState }) {
    let columnOrder = [
      'selector',
      'live',
      'project',
      'folder',
      'reference',
      'lastUpdated',
      'riskAssessments',
      'reviewDate',
      'approvalStatus',
      'creator',
      'creationDate',
      'startDate',
      'reviewer',
      'signatureStatus',
      'approver',
      'actions',
    ];

    dashboardConfigurableFields.forEach((dashboardConfigurableField) => {
      const columnId = dashboardConfigurableFieldKey({
        dashboardConfigurableField,
      });
      const reorderInsertColumnIndex = columnOrder.indexOf('folder') + 1;

      columnOrder = [
        ...columnOrder.slice(0, reorderInsertColumnIndex),
        columnId,
        ...columnOrder.slice(reorderInsertColumnIndex),
      ];
    });

    if (formattedTableColumnConfigurations.size > 0) {
      const reorderInsertColumnIndex = columnOrder.indexOf('project') + 1;

      columnOrder = [
        ...new Set([
          ...columnOrder.slice(0, reorderInsertColumnIndex),
          ...formattedTableColumnConfigurations.keys(),
          ...columnOrder.slice(reorderInsertColumnIndex),
        ]),
      ];
    }

    draftState.columnOrder = columnOrder.filter((c) => !hiddenColumns.has(c));
  }

  function assignColumnVisibility({ draftState }) {
    let columnVisibility = {
      selector: true,
      live: true,
      project: true,
      folder: false,
      reference: false,
      lastUpdated: false,
      riskAssessments: false,
      reviewDate: false,
      approvalStatus: false,
      creator: false,
      creationDate: false,
      startDate: false,
      reviewer: false,
      signatureStatus: false,
      approver: false,
      actions: true,
    };

    if (formattedTableColumnConfigurations.size > 0) {
      dashboardConfigurableFields.forEach((dashboardConfigurableField) => {
        const columnId = dashboardConfigurableFieldKey({
          dashboardConfigurableField,
        });

        columnVisibility[columnId] = false;
      });

      [...formattedTableColumnConfigurations.keys()].forEach((columnId) => {
        columnVisibility[columnId] = true;
      });
    } else {
      dashboardConfigurableFields.forEach((dashboardConfigurableField) => {
        const columnId = dashboardConfigurableFieldKey({
          dashboardConfigurableField,
        });

        columnVisibility[columnId] = true;
      });

      columnVisibility = {
        ...columnVisibility,
        ...{
          folder: true,
          reference: true,
          lastUpdated: true,
          riskAssessments: true,
          reviewDate: true,
          approvalStatus: true,
        },
      };
    }

    draftState.columnVisibility = Object.keys(columnVisibility)
      .filter((c) => !hiddenColumns.has(c))
      .reduce((obj, c) => ({ ...obj, [c]: columnVisibility[c] }), {});
  }

  function assignColumnSort({ draftState }) {
    const columnSort = {
      columnId: 'lastUpdated',
      direction: 'applied',
      isFutureDirectionShownOnHover: true,
      isUnsorted: true,
    };

    if (
      formattedTableSortConfiguration &&
      !hiddenColumns.has(formattedTableSortConfiguration.columnId)
    ) {
      columnSort.columnId = formattedTableSortConfiguration.columnId;
      columnSort.direction = formattedTableSortConfiguration.direction;
      columnSort.isUnsorted = false;
    }

    draftState.columnSort = columnSort;
  }

  function assignColumnSizingDefaults({ draftState }) {
    const columnSizingDefaults = {
      selector: { size: 40, minSize: 40, maxSize: 40 },
      live: { size: 42, minSize: 42, maxSize: 42 },
      project: { size: 560, minSize: 42, maxSize: 560 },
      folder: { size: 120, minSize: 42, maxSize: 560 },
      reference: { size: 112, minSize: 42, maxSize: 560 },
      lastUpdated: { size: 144, minSize: 42, maxSize: 280 },
      riskAssessments: { size: 152, minSize: 42, maxSize: 280 },
      reviewDate: { size: 144, minSize: 42, maxSize: 280 },
      approvalStatus: { size: 116, minSize: 88, maxSize: 280 },
      creator: { size: 120, minSize: 42, maxSize: 560 },
      creationDate: { size: 144, minSize: 42, maxSize: 280 },
      startDate: { size: 144, minSize: 42, maxSize: 280 },
      reviewer: { size: 120, minSize: 42, maxSize: 560 },
      signatureStatus: { size: 144, minSize: 42, maxSize: 280 },
      approver: { size: 120, minSize: 42, maxSize: 560 },
      actions: { size: 50, minSize: 50, maxSize: 50 },
    };

    dashboardConfigurableFields.forEach((dashboardConfigurableField) => {
      const columnId = dashboardConfigurableFieldKey({
        dashboardConfigurableField,
      });

      columnSizingDefaults[columnId] = { size: 120, minSize: 42, maxSize: 560 };
    });

    draftState.columnSizingDefaults = columnSizingDefaults;
  }

  function assignColumnSizing({ draftState }) {
    draftState.columnSizing = Object.keys(
      draftState.columnSizingDefaults,
    ).reduce((acc, columnId) => {
      if (draftState.columnVisibility[columnId])
        acc[columnId] =
          formattedTableColumnConfigurations.get(columnId) ||
          draftState.columnSizingDefaults[columnId].size;

      return acc;
    }, {});
  }

  function assignColumns({ draftState }) {
    function buildColumnSizingByColumnId(columnId) {
      return {
        size:
          draftState.columnSizing[columnId] ||
          draftState.columnSizingDefaults[columnId].size,
        minSize: draftState.columnSizingDefaults[columnId].minSize,
        maxSize: draftState.columnSizingDefaults[columnId].maxSize,
      };
    }

    const columns = [
      {
        id: 'selector',
        header: ({ table }) => {
          if (table.options.meta.isDataPending) return null;

          const projectIds = table.options.data.map((project) => project.id);

          const { selectedProjectIds } = table.options.meta;

          const areAllProjectsSelected = projectIds.every((projectId) =>
            selectedProjectIds.includes(projectId),
          );

          return (
            <div className='tw-p-2 tw-pl-4'>
              <button
                className={classNames(
                  'tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-sm tw-border-2 tw-border-solid tw-p-0',
                  selectedProjectIds.length > 0 ?
                    'tw-border-blue-400 tw-bg-blue-400'
                  : 'tw-border-grey-300 tw-bg-white hover:tw-border-blue-300',
                )}
                onClick={() =>
                  table.options.meta.onSelectAllProjectsToggle({
                    projectIds,
                  })
                }
              >
                {selectedProjectIds.length > 0 ?
                  <>
                    {areAllProjectsSelected ?
                      <CheckIcon className='tw-h-2 tw-min-w-[10px]' />
                    : <MinusIcon className='tw-h-2 tw-min-w-[10px]' />}
                    <span className='tw-sr-only'>Unselect all</span>
                  </>
                : <span className='tw-sr-only'>Select all</span>}
              </button>
            </div>
          );
        },
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return null;

          const { id } = row.original;

          return (
            <div className='tw-pointer-events-none tw-flex tw-h-full tw-items-center tw-pl-4 tw-pr-2 hover:tw-bg-blue-050'>
              <button
                className={classNames(
                  'tw-pointer-events-auto tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-sm tw-border-2 tw-border-solid tw-p-0',
                  table.options.meta.selectedProjectIds.includes(id) ?
                    'tw-border-blue-400 tw-bg-blue-400'
                  : 'tw-border-grey-300 tw-bg-white hover:tw-border-blue-300',
                )}
                onClick={() => table.options.meta.onProjectToggle({ id })}
              >
                {table.options.meta.selectedProjectIds.includes(id) ?
                  <>
                    <CheckIcon className='tw-h-2 tw-min-w-[10px]' />
                    <span className='tw-sr-only'>Unselect</span>
                  </>
                : <span className='tw-sr-only'>Select</span>}
              </button>
            </div>
          );
        },
        enableResizing: false,
        ...buildColumnSizingByColumnId('selector'),
      },
      {
        id: 'live',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            isSortVisible={false}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <DotSkeleton />;

          const status = _.camelCase(
            row.original.meta.calculatedLifecycleStatus,
          );

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-flex tw-h-full tw-items-center tw-justify-center tw-px-2'>
                <LifecycleStatusIndicator
                  ramsSingularName={singularRAMSName}
                  status={status}
                />
              </div>
            </a>
          );
        },
        enableResizing: false,
        ...buildColumnSizingByColumnId('live'),
      },
      {
        id: 'project',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-900'>
                {row.original.attributes.name}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('project'),
      },
      {
        id: 'folder',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          const folder =
            table.options.meta.included.folder[
              row.original.relationships.folder?.data?.id
            ];

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {folder?.attributes.name}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('folder'),
      },
      {
        id: 'reference',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {row.original.attributes.reference}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('reference'),
      },
      {
        id: 'lastUpdated',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {moment(row.original.attributes.modified).format(
                  'DD MMM YY [at] HH:mm',
                )}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('lastUpdated'),
      },
      {
        id: 'riskAssessments',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <DotTextSkeleton />;

          const status = _.camelCase(
            row.original.meta.calculatedRiskAssessmentsStatus,
          );

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-flex tw-h-full tw-items-center tw-justify-start tw-px-2'>
                {status && status !== 'noStatus' && (
                  <RiskAssessmentsStatusIndicator status={status} />
                )}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('riskAssessments'),
      },
      {
        id: 'reviewDate',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <DotTextSkeleton />;

          const scheduledContentReviewRequest =
            table.options.meta.included.projectContentReviewRequest[
              row.original.relationships.scheduledContentReviewRequest?.data?.id
            ];
          const dueDate =
            scheduledContentReviewRequest?.attributes.dueDate &&
            moment(scheduledContentReviewRequest.attributes.dueDate).format(
              'DD MMM YY',
            );
          const status = _.camelCase(row.original.meta.calculatedReviewStatus);

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-flex tw-h-full tw-items-center tw-justify-start tw-px-2'>
                {status && status !== 'noStatus' && (
                  <ReviewStatusIndicator dueDate={dueDate} status={status} />
                )}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('reviewDate'),
      },
      {
        id: 'approvalStatus',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          const status = _.camelCase(row.original.meta.approvalStatus);

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-flex tw-h-full tw-items-center tw-justify-start tw-px-2'>
                {status && <ApprovalStatusIndicator status={status} />}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('approvalStatus'),
      },
      {
        id: 'creator',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          const creator =
            table.options.meta.included.simpleUser[
              row.original.relationships.user?.data?.id
            ];

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {creator && formatDomainUserFullName({ domainUser: creator })}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('creator'),
      },
      {
        id: 'creationDate',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {moment(row.original.attributes.createdAt).format(
                  'DD MMM YY [at] HH:mm',
                )}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('creationDate'),
      },
      {
        id: 'startDate',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {row.original.attributes.startDate &&
                  moment(row.original.attributes.startDate).format(
                    'DD MMM YY [at] HH:mm',
                  )}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('startDate'),
      },
      {
        id: 'reviewer',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          const scheduledContentReviewRequest =
            table.options.meta.included.projectContentReviewRequest[
              row.original.relationships.scheduledContentReviewRequest?.data?.id
            ];

          const reviewer =
            table.options.meta.included.simpleUser[
              scheduledContentReviewRequest?.relationships?.assignee?.data?.id
            ];

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {reviewer && formatDomainUserFullName({ domainUser: reviewer })}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('reviewer'),
      },
      {
        id: 'signatureStatus',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <DotTextSkeleton />;

          const status = _.camelCase(
            row.original.meta.calculatedSignatureStatus,
          );

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-flex tw-h-full tw-items-center tw-justify-start tw-px-2'>
                {status && status !== 'noStatus' && (
                  <SignatureStatusIndicator status={status} />
                )}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('signatureStatus'),
      },
      {
        id: 'approver',
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          const approver =
            table.options.meta.included.simpleUser[
              row.original.relationships.calculatedApprover?.data?.id
            ];

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {approver && formatDomainUserFullName({ domainUser: approver })}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId('approver'),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row, table }) => (
          <ProjectsTableRowActions
            row={row}
            singularRAMSName={singularRAMSName}
            table={table}
          />
        ),
        enableResizing: false,
        ...buildColumnSizingByColumnId('actions'),
      },
    ];

    dashboardConfigurableFields.forEach((originalField) => {
      const columnId = dashboardConfigurableFieldKey({
        dashboardConfigurableField: originalField,
      });

      columns.push({
        id: columnId,
        header: ({ column, table }) => (
          <ProjectsTableHeader
            columnId={column.id}
            columnName={columnNames[column.id]}
            columnSort={table.options.meta.columnSort}
            isSortEnabled={table.options.meta.isSortEnabled}
            onColumnSortChange={table.options.meta.onColumnSortChange}
          />
        ),
        cell: ({ row, table }) => {
          if (table.options.meta.isDataPending) return <TextSkeleton />;

          const projectField =
            table.options.meta.projectFieldIndexedByOriginalField[
              row.original.id
            ]?.[originalField.id];

          return (
            <a href={`/projects/${row.original.id}`}>
              <div className='tw-h-full tw-content-center tw-truncate tw-px-2 tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-700'>
                {projectField?.attributes.value}
              </div>
            </a>
          );
        },
        ...buildColumnSizingByColumnId(columnId),
      });
    });

    draftState.columns = columns.filter((c) => !hiddenColumns.has(c.id));
  }
}
